import { TAG_TYPES } from '@/lib/api/tags';
import { processServerError, handledError } from '@/lib/api/listQuery';
import { buildLinkableShipmentFilters } from '@/lib/mappers/links';
import { buildPendingOrderFiltersInput } from '@/lib/mappers/filters';
import { notifyOnSuccess } from '@/lib/api/notifyOnSuccess';
import { processFulfilledAllocationIds } from '@/lib/mappers/fulfill';
import {
  processOrdersPage,
  processOrderFilterOptions,
  processOrderDetail,
  processOrderLinkedShipment,
  processOrderAllocation,
  processOrderForm,
  buildOrderDto,
  buildPendingOrdersQuery,
} from '../mappers/ordersMappers';
import { processOrderForUpdateShipment } from '../mappers/updateShipmentMappers';
import {
  processOrderForFulfill,
  processLinkableShipmentsForFulfill,
  buildFulfillOrderDto,
} from '../mappers/fulfillOrderMappers';
import { buildOrderShipmentDto } from '@/lib/mappers/orderShipments';
// The writes stay REST under the module segment; every read is GraphQL.
const PATHS = {
  list: (module) => `/${module}/pending-orders`,
  detail: (module, id) => `/${module}/pending-orders/${id}`,
  // Update shipment: creates the shipment under the order, links it and advances its pending-shipment allocations.
  orderShipments: (module, id) => `/${module}/pending-orders/${id}/shipments`,
  dispatch: (module, id) => `/${module}/pending-orders/${id}/dispatch`,
};

// The filter panel's Material and Customer choices — only what the table's orders carry.
const PENDING_ORDER_FILTER_OPTIONS_QUERY = `
  query PendingOrderFilterOptions($certificateType: CertificateType, $filters: PendingOrderFilters) {
    pendingOrderFilterOptions(certificateType: $certificateType, filters: $filters) {
      materials { id name }
      customers { id name }
    }
  }
`;

// The fields the Orders table renders; `PendingOrder` has 40+.
const PENDING_ORDERS_QUERY = `
  query PendingOrders($certificateType: CertificateType, $limit: Int, $page: Int, $filters: PendingOrderFilters, $search: PendingOrderSearch, $orderBy: [PendingOrderOrderBy!]) {
    pendingOrders(certificateType: $certificateType, limit: $limit, page: $page, filters: $filters, search: $search, orderBy: $orderBy) {
      nodes {
        id
        orderNo
        orderStatus
        status
        material { id name }
        site { id siteName }
        backToBackAccount { accountName }
        terminal
        customerAccount { id companyname }
        shippingDetails { shippingTo }
        avgGhgSaving
        workingUnit
        declarationUnit
        contractedWorkingQuantity
        contractedDeclarationQuantity
        shippedWorkingQuantity
        shippedDeclarationQuantity
        remainingToShipWorkingQuantity
        remainingToShipDeclarationQuantity
        allocatedWorkingQuantity
        allocatedDeclarationQuantity
        remainingToAllocateWorkingQuantity
        remainingToAllocateDeclarationQuantity
        tolerance
        ghgEmission
        ghgSaving
        rawMaterialType
        countryOfOrigin
        feedstockCategory
        preferredAllocation
        dispatchDate
        discrepancyCount
      }
      pageInfo {
        total
        page
        limit
        isEmpty
      }
    }
  }
`;

// Order Details tab, meta bar and header — pendingOrder(id) has no certificateType/module argument at all (account-scoped only, not yet module-scoped on the backend).
const PENDING_ORDER_QUERY = `
  query PendingOrder($id: ID!) {
    pendingOrder(id: $id) {
      id
      orderNo
      orderStatus
      status
      material { id name }
      site { id siteName }
      backToBackAccount { accountName }
      terminal
      customerAccount { id companyname }
      shippingDetails { shippingTo }
      workingUnit
      declarationUnit
      contractedWorkingQuantity
      contractedDeclarationQuantity
      shippedWorkingQuantity
      shippedDeclarationQuantity
      remainingToShipWorkingQuantity
      remainingToShipDeclarationQuantity
      allocatedWorkingQuantity
      allocatedDeclarationQuantity
      remainingToAllocateWorkingQuantity
      remainingToAllocateDeclarationQuantity
      tolerance
      ghgEmission
      ghgSaving
      avgGhgSaving
      rawMaterialType
      countryOfOrigin
      feedstockCategory
      preferredAllocation
      mandatory {
        maxGhgEmission
        minGhgSaving
        rawMaterialType
        countryOfOrigin
      }
      dispatchDate
      discrepancyCount
    }
  }
`;

// Add/Edit form prefill — only the ids and raw values a dropdown/input needs, mirroring CONTRACT_QUERY's shape.
const PENDING_ORDER_FORM_QUERY = `
  query PendingOrderForm($id: ID!) {
    pendingOrder(id: $id) {
      id
      orderNo
      site { id }
      backToBackAccountId
      terminalRefId
      material { id }
      customerAccount { id }
      shippingDetails { id }
      tolerance
      contractedWorkingQuantity
      contractedDeclarationQuantity
      conversionFactor
      workingUnit
      declarationUnit
      shippedWorkingQuantity
      shippedDeclarationQuantity
      allocatedWorkingQuantity
      allocatedDeclarationQuantity
      ghgEmission
      ghgSaving
      rawMaterialType
      countryOfOrigin
      feedstockCategory
      preferredAllocation
      mandatory {
        maxGhgEmission
        minGhgSaving
        rawMaterialType
        countryOfOrigin
      }
      dispatchDate
    }
  }
`;

// Update shipment prefill — only the ids the shipment form needs to lock its inherited fields.
const PENDING_ORDER_FOR_SHIPMENT_QUERY = `
  query PendingOrderForShipment($id: ID!) {
    pendingOrder(id: $id) {
      id
      orderNo
      site { id }
      backToBackAccountId
      terminalRefId
      terminal
      material { id }
      customerAccount { id }
      shippingDetails { id }
    }
  }
`;

// Fulfill order page — a superset of PENDING_ORDER_QUERY and PENDING_ORDER_FOR_SHIPMENT_QUERY above, so it can't disagree with either.
const PENDING_ORDER_FOR_FULFILL_QUERY = `
  query PendingOrderForFulfill($id: ID!) {
    pendingOrder(id: $id) {
      id
      orderNo
      material { id name }
      site { id siteName }
      backToBackAccountId
      backToBackAccount { accountName }
      terminalRefId
      terminal
      customerAccount { id companyname }
      shippingDetails { shippingTo }
      workingUnit
      declarationUnit
      contractedWorkingQuantity
      contractedDeclarationQuantity
      shippedWorkingQuantity
      shippedDeclarationQuantity
      remainingToShipWorkingQuantity
      remainingToShipDeclarationQuantity
      allocatedWorkingQuantity
      allocatedDeclarationQuantity
      remainingToAllocateWorkingQuantity
      remainingToAllocateDeclarationQuantity
      tolerance
      ghgEmission
      ghgSaving
      rawMaterialType
      countryOfOrigin
      feedstockCategory
      preferredAllocation
      mandatory {
        maxGhgEmission
        minGhgSaving
        rawMaterialType
        countryOfOrigin
      }
      dispatchDate
      discrepancyCount
    }
  }
`;

// Fulfill ▸ Select outgoing shipment — no `linkableShipmentsForOrder`, so the match rides on `outgoingShipments`, as the Link shipments wizard's step 1 already does.
const LINKABLE_SHIPMENTS_FOR_FULFILL_QUERY = `
  query LinkableShipmentsForFulfill($certificateType: CertificateType, $filters: OutgoingShipmentFilters) {
    outgoingShipments(certificateType: $certificateType, limit: 100, filters: $filters) {
      nodes {
        id
        shipmentNo
        shipmentStatus
        material { name }
        site { siteName }
        backToBackAccountId
        backToBackAccount { accountName }
        terminal
        customerAccount { companyname }
        shippingDetails { shippingTo }
        workingUnit
        declarationUnit
        assignedQuantityWorking
        assignedQuantityDeclaration
        totalQuantityWorking
        totalQuantityDeclaration
        remainingToAllocateQuantityWorking
        remainingToAllocateQuantityDeclaration
        unassignedQuantityWorking
        unassignedQuantityDeclaration
        linkedOrders { id }
        dispatchDate
        discrepancyCount
      }
      pageInfo { total }
    }
  }
`;

// The backend caps a page at 100 and this tab is unpaginated.
const ORDER_ALLOCATIONS_LIMIT = 100;

// The fields the Allocation details tab renders; `Fulfillment` has 45+.
const ORDER_ALLOCATIONS_QUERY = `
  query AllocationDetailsForOrder($orderId: ID!, $limit: Int) {
    allocationDetailsForOrder(orderId: $orderId, limit: $limit) {
      nodes {
        id
        transactionId
        approvalStatus
        canGenerateDeclaration
        canRevoke
        sentFrom
        receivedAt
        shippingTo
        shippingToSites
        transactionDate
        workingUnit
        declarationUnit
        certifiedQuantityWorking
        certifiedQuantityDeclaration
        massBalancePeriodLabel
        reference { orderNo shipmentNo }
        associatedDeclarationNo
        transactedBy { name email }
        countryOfOrigin
        rawMaterialType
        ghgEmission
        ghgEmissionSavings
        feedstockCategory
        allocatedQuota
        dateOfDispatch
        daysLeft
      }
    }
  }
`;

// Linked outgoing shipment tab — one OrderLinkedShipment node per linked shipment, unpaginated (no limit/offset sent).
const LINKED_OUTGOING_SHIPMENTS_QUERY = `
  query LinkedOutgoingShipmentsForOrder($orderId: ID!) {
    linkedOutgoingShipmentsForOrder(orderId: $orderId) {
      nodes {
        assignedQuantityWorking
        assignedQuantityDeclaration
        workingUnit
        declarationUnit
        avgGhgSaving
        tolerance
        shipment {
          id
          shipmentNo
          status
          shippingDetails { shippingTo }
          totalQuantityWorking
          totalQuantityDeclaration
          pendingQuantityWorking
          pendingQuantityDeclaration
          allocatedQuantityWorking
          allocatedQuantityDeclaration
          remainingToAllocateQuantityWorking
          remainingToAllocateQuantityDeclaration
          dispatchDate
          discrepancyCount
        }
      }
      pageInfo {
        total
        isEmpty
      }
    }
  }
`;

export const ordersEndpoints = (build) => ({
  // JSON, not multipart — the order form has no attachments.
  createOrder: build.mutation({
    query: ({ module, values, equivalentUnit }) => ({
      url: PATHS.list(module),
      method: 'POST',
      body: buildOrderDto(values, { equivalentUnit }),
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('ORDERS_SAVED'),
    invalidatesTags: [{ type: TAG_TYPES.Order, id: 'LIST' }],
  }),

  getOrders: build.query({
    // `document` routes this through GraphQL; the module scopes it as `certificateType`.
    query: (screenState) => ({
      document: PENDING_ORDERS_QUERY,
      variables: buildPendingOrdersQuery(screenState),
    }),
    transformResponse: (data) => processOrdersPage(data.pendingOrders),
    providesTags: (result) =>
      result
        ? [
            ...result.rows.map((row) => ({ type: TAG_TYPES.Order, id: row.id })),
            { type: TAG_TYPES.Order, id: 'LIST' },
          ]
        : [{ type: TAG_TYPES.Order, id: 'LIST' }],
  }),

  // Takes the page's fixed scope only — a picked filter here would hide its siblings.
  getOrderFilterOptions: build.query({
    query: ({ certificateType, extraFilters }) => ({
      document: PENDING_ORDER_FILTER_OPTIONS_QUERY,
      variables: { certificateType, filters: buildPendingOrderFiltersInput({}, extraFilters) },
    }),
    transformResponse: (data) => processOrderFilterOptions(data.pendingOrderFilterOptions),
    providesTags: [{ type: TAG_TYPES.Order, id: 'LIST' }],
  }),

  getOrderDetail: build.query({
    query: ({ id }) => ({ document: PENDING_ORDER_QUERY, variables: { id } }),
    transformResponse: (data) => processOrderDetail(data.pendingOrder),
    providesTags: (result, error, { id }) => [{ type: TAG_TYPES.Order, id }],
  }),

  // No Order tag: <Form> seeds once, so a save's invalidation buys a refetch nothing reads.
  getOrderForm: build.query({
    query: ({ id }) => ({ document: PENDING_ORDER_FORM_QUERY, variables: { id } }),
    transformResponse: (data) => processOrderForm(data.pendingOrder),
    transformErrorResponse: handledError,
  }),

  updateOrder: build.mutation({
    query: ({ module, id, values, equivalentUnit }) => ({
      url: PATHS.detail(module, id),
      method: 'PUT',
      body: buildOrderDto(values, { equivalentUnit }),
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('ORDERS_UPDATED'),
    invalidatesTags: (result, error, arg) => [
      { type: TAG_TYPES.Order, id: arg.id },
      { type: TAG_TYPES.Order, id: 'LIST' },
    ],
  }),

  // Rejected server-side once shipped/allocated — see orderActions.js, which mirrors that here.
  deleteOrder: build.mutation({
    query: ({ module, id }) => ({
      url: PATHS.detail(module, id),
      method: 'DELETE',
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('ORDERS_DELETED'),
    invalidatesTags: (result, error, arg) => [
      { type: TAG_TYPES.Order, id: arg.id },
      { type: TAG_TYPES.Order, id: 'LIST' },
    ],
  }),

  getOrderLinkedShipments: build.query({
    query: ({ id }) => ({ document: LINKED_OUTGOING_SHIPMENTS_QUERY, variables: { orderId: id } }),
    transformResponse: (data) =>
      (data?.linkedOutgoingShipmentsForOrder?.nodes ?? []).map(processOrderLinkedShipment),
    providesTags: (result, error, { id }) => [
      { type: TAG_TYPES.OutgoingShipment, id: `ORDER-${id}` },
      // Deleting a shipment unlinks it without knowing which orders held it.
      { type: TAG_TYPES.OutgoingShipment, id: 'LIST' },
    ],
  }),

  // The tab shows its own failure, so handledError opts it out of notifyApiErrors.
  getOrderAllocations: build.query({
    query: ({ id }) => ({
      document: ORDER_ALLOCATIONS_QUERY,
      variables: { orderId: id, limit: ORDER_ALLOCATIONS_LIMIT },
    }),
    transformResponse: (data) =>
      (data?.allocationDetailsForOrder?.nodes ?? []).map(processOrderAllocation),
    transformErrorResponse: handledError,
    providesTags: (result, error, { id }) => [
      { type: TAG_TYPES.Allocation, id: `ORDER-${id}` },
      // Revoke and generate-declaration know the allocation, never which order holds it.
      { type: TAG_TYPES.Allocation, id: 'LIST' },
    ],
  }),
  getOrderForShipment: build.query({
    query: ({ id }) => ({ document: PENDING_ORDER_FOR_SHIPMENT_QUERY, variables: { id } }),
    transformResponse: (data) => processOrderForUpdateShipment(data.pendingOrder),
    transformErrorResponse: handledError,
    providesTags: (result, error, { id }) => [{ type: TAG_TYPES.Order, id }],
  }),
  getOrderForFulfill: build.query({
    query: ({ id }) => ({ document: PENDING_ORDER_FOR_FULFILL_QUERY, variables: { id } }),
    transformResponse: (data) => processOrderForFulfill(data.pendingOrder),
    transformErrorResponse: handledError,
    providesTags: (result, error, { id }) => [{ type: TAG_TYPES.Order, id }],
  }),
  createShipmentForOrder: build.mutation({
    query: ({ module, orderId, values }) => ({
      url: PATHS.orderShipments(module, orderId),
      method: 'POST',
      body: buildOrderShipmentDto(values),
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('OUTGOING_SHIPMENTS_SAVED_FLASH'),
    invalidatesTags: (result, error, arg) => [
      { type: TAG_TYPES.Order, id: arg.orderId },
      { type: TAG_TYPES.OutgoingShipment, id: 'LIST' },
      { type: TAG_TYPES.OutgoingShipment, id: `ORDER-${arg.orderId}` },
      // The route also moves the order's pending-shipment allocations on.
      { type: TAG_TYPES.Allocation, id: 'LIST' },
    ],
  }),
  // The picker shows every eligible shipment at once, so it asks for one page of 100.
  getLinkableOutgoingShipmentsForOrder: build.query({
    query: ({ certificateType, match }) => ({
      document: LINKABLE_SHIPMENTS_FOR_FULFILL_QUERY,
      variables: { certificateType, filters: buildLinkableShipmentFilters(match) },
    }),
    transformResponse: (data, meta, { match, id }) =>
      processLinkableShipmentsForFulfill(data.outgoingShipments, match, id),
    providesTags: (result, error, { id }) => [
      { type: TAG_TYPES.OutgoingShipment, id: `ORDER-${id}` },
    ],
  }),
  dispatchOrder: build.mutation({
    query: ({ module, id, values }) => ({
      url: PATHS.dispatch(module, id),
      method: 'POST',
      body: buildFulfillOrderDto(values),
    }),
    // The dispatch route nests the fulfil's own result under `fulfill`.
    transformResponse: (dto) => processFulfilledAllocationIds(dto?.output?.data?.fulfill),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('ORDERS_FULFILL_SAVED_FLASH'),
    invalidatesTags: (result, error, { id }) => [
      { type: TAG_TYPES.Order, id },
      { type: TAG_TYPES.Order, id: 'LIST' },
      { type: TAG_TYPES.OutgoingShipment, id: 'LIST' },
      // A fulfil links a shipment and allocates, so both of the order's tabs are stale.
      { type: TAG_TYPES.OutgoingShipment, id: `ORDER-${id}` },
      { type: TAG_TYPES.Allocation, id: `ORDER-${id}` },
      { type: TAG_TYPES.Allocation, id: 'LIST' },
    ],
  }),
});
